import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ResponseFacilityItem } from '§A/facility/types/response.type'
import type { Uuid4 } from '§C/types/system.type'

export const useStoreFacility = defineStore('facility', () => {
  // State
  const facilities = ref<ResponseFacilityItem[]>([])
  const selectedFacilityIds = ref<Set<Uuid4>>(new Set())

  // Getters
  const facilitiesCount = computed(() => facilities.value.length)

  const selectedFacilities = computed(() =>
    facilities.value.filter(facility => selectedFacilityIds.value.has(facility.uuid))
  )

  const selectedFacilityCount = computed(() => selectedFacilityIds.value.size)

  const getFacilityById = computed(() => {
    return (facilityId: Uuid4) => facilities.value.find(facility => facility.uuid === facilityId)
  })

  const isFacilitySelected = computed(() => {
    return (facilityId: Uuid4) => selectedFacilityIds.value.has(facilityId)
  })

  // The location an Axios facility (project) is linked to
  const getLocationOfProject = computed(() => {
    return (projectUuid: Uuid4) => facilities.value.find(facility => facility.projects?.includes(projectUuid))
  })

  // Actions
  const setFacilities = (newFacilities: ResponseFacilityItem[]) => {
    facilities.value = [...newFacilities]
  }

  const addFacility = (facility: ResponseFacilityItem) => {
    const existingIndex = facilities.value.findIndex(f => f.uuid === facility.uuid)

    if (existingIndex >= 0) {
      // Update existing facility
      facilities.value[existingIndex] = facility
    } else {
      // Add new facility
      facilities.value.push(facility)
    }
  }

  const updateFacility = (facility: ResponseFacilityItem) => {
    const index = facilities.value.findIndex(f => f.uuid === facility.uuid)

    if (index >= 0) {
      facilities.value[index] = facility
    } else {
      console.warn(`Facility with ID ${facility.uuid} not found in store`)
    }
  }

  const removeFacility = (facilityId: Uuid4) => {
    const index = facilities.value.findIndex(f => f.uuid === facilityId)

    if (index >= 0) {
      facilities.value.splice(index, 1)
      // Also remove from selected if it was selected
      selectedFacilityIds.value.delete(facilityId)
    }
  }

  const clearFacilities = () => {
    facilities.value = []
    selectedFacilityIds.value.clear()
  }

  // Selection management
  const selectFacility = (facilityId: Uuid4) => {
    selectedFacilityIds.value.add(facilityId)
  }

  const deselectFacility = (facilityId: Uuid4) => {
    selectedFacilityIds.value.delete(facilityId)
  }

  const toggleFacilitySelection = (facilityId: Uuid4) => {
    if (selectedFacilityIds.value.has(facilityId)) {
      selectedFacilityIds.value.delete(facilityId)
    } else {
      selectedFacilityIds.value.add(facilityId)
    }
  }

  const selectAllFacilities = () => {
    facilities.value.forEach(facility => {
      selectedFacilityIds.value.add(facility.uuid)
    })
  }

  const deselectAllFacilities = () => {
    selectedFacilityIds.value.clear()
  }

  const setSelectedFacilities = (facilityIds: Uuid4[]) => {
    selectedFacilityIds.value = new Set(facilityIds)
  }

  const getSelectedFacilityIds = () => {
    return Array.from(selectedFacilityIds.value)
  }

  return {
    // State
    facilities,
    selectedFacilityIds,

    // Getters
    facilitiesCount,
    selectedFacilities,
    selectedFacilityCount,
    getFacilityById,
    isFacilitySelected,
    getLocationOfProject,

    // Actions
    setFacilities,
    addFacility,
    updateFacility,
    removeFacility,
    clearFacilities,

    // Selection management
    selectFacility,
    deselectFacility,
    toggleFacilitySelection,
    selectAllFacilities,
    deselectAllFacilities,
    setSelectedFacilities,
    getSelectedFacilityIds
  }
})